import { sanitizeHtml } from "utils/sanitize";
import { graphQlClient } from "lib/graphql-client.js";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";

import Layout from "../components/Layout";

const SocialLinks = dynamic(() =>
  import("components/links/SocialLinks/SocialLinks")
);

const FormContainer = dynamic(() =>
  import("components/forms/FormContainer/FormContainer")
);
const ContactForm = dynamic(() =>
  import("components/forms/ContactForm/ContactForm")
);
import { contactFormSchema } from "components/forms/ContactForm/contactFormSchema";
import { processHubspotForm } from "lib/api/hubspot/form-submit";
import SectionTitle from "components/typography/SectionTitle/SectionTitle";
import IconBox from "components/content/IconBox/IconBox";
import { fetchLayoutData, fetchLayoutDataServer } from "lib/api/strapi-layout";

import { BsChatLeftText, BsMailbox } from "react-icons/bs";
import { FiShare2 } from "react-icons/fi";

import styles from "./contact.module.scss";

const ContactUs = ({ contactData, layoutData }) => {
  const SEO = contactData?.search_engine_optimization;
  return (
    <Layout seo={SEO} layoutData={layoutData}>
      <div className={`${styles.contactArea}`}>
        <div className={`variable-container ${styles.container}`}>
          <div className={`${styles.contactColumns}`}>
            <div className={`${styles.formContainer}`}>
              <h1 className={`${styles.title}`}>
                <SectionTitle title="Contact Us" />
              </h1>
              {/* <HubspotFormEmbed
                region="na1"
                portalId="20685762"
                formId="6dc21cb3-0d4a-4e66-a641-899c3f534f01"
                target="#hubspotContactForm"
                scriptSrc="//js.hsforms.net/forms/v2.js?pre=1"
                cssClass="hubspotFormEmbed"
              /> */}
              <FormContainer
                formId="6dc21cb3-0d4a-4e66-a641-899c3f534f01"
                pageName={SEO.metaTitle}
                submitHandler={processHubspotForm}
                schema={contactFormSchema}
              >
                <ContactForm />
              </FormContainer>
              <div>
                <p>
                  WHERENEXT EXPERIENCES PTY. LTD. needs the contact information
                  you provide to us to contact you about our products and
                  services. You may unsubscribe from these communications at any
                  time. For information on how to unsubscribe, as well as our
                  privacy practices and commitment to protecting your privacy,
                  please review our <Link href="/privacy">Privacy Policy</Link>.
                </p>
              </div>
            </div>
            <div className={`${styles.contactInfoContainer}`}>
              <div className={`${styles.text}`}>
                <p>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(
                        contactData?.contactContent?.description
                      ),
                    }}
                  />
                </p>
                <IconBox
                  iconImage={
                    <Image
                      width={30}
                      height={30}
                      src={"/assets/img/icons/mail_icon.svg"}
                      style={{ display: "block" }}
                      alt="Mail Icon"
                    />
                  }
                  title="Email Us"
                  // text="You can reach us by email, WhatsApp, and Facebook Messenger"
                  textColor="var(--light-text)"
                  divider={true}
                >
                  <Link
                    href={`mailto:${contactData?.contactContent?.contactEmail}`}
                    aria-label="social-icon-mail"
                    className={`${styles.mailLink}`}
                  >
                    <div className={`${styles.emailAddress}`}>
                      {contactData?.contactContent?.contactEmail}
                    </div>
                  </Link>
                </IconBox>
                <IconBox
                  iconImage={
                    <BsChatLeftText size={25} color="var(--light-text)" />
                  }
                  title="Chat With Us"
                  text="You can reach us on WhatsApp, and Facebook Messenger"
                  textColor="var(--light-text)"
                  divider={true}
                >
                  <div className={`${styles.socialLinks}`}>
                    <SocialLinks
                      links={layoutData?.footerData?.socialLinks}
                      include={["whatsapp", "messenger"]}
                      iconColor="var(--light-text)"
                      // itemStyles={{ padding: "0 var(--spacing-xs) 0 0" }}
                    />
                  </div>
                </IconBox>

                <IconBox
                  iconImage={<BsMailbox size={25} color="var(--light-text)" />}
                  title="Send Us Snail Mail"
                  textColor="var(--light-text)"
                  highlightWords="2"
                  divider={true}
                  // text="You can reach us by email, WhatsApp, and Facebook Messenger"
                >
                  <div className={`${styles.address}`}>
                    <address
                      style={{ fontStyle: "normal", lineHeight: "140%" }}
                    >
                      WhereNext Experiences Pty. Ltd.
                      <br />
                      5/5 Carlisle Street
                      <br />
                        Tamarama, NSW, 2026                      
                      <br />
                      Australia 
                    </address>
                  </div>
                </IconBox>
                <IconBox
                  iconImage={<FiShare2 size={25} color="var(--light-text)" />}
                  title="Follow Us"
                  text="Connect with us on social media"
                  textColor="var(--light-text)"
                  divider={true}
                >
                  <div className={`${styles.socialLinks}`}>
                    <SocialLinks
                      links={layoutData?.footerData?.socialLinks}
                      // omit={["email", "whatsapp", "messenger"]}
                      include={["instagram", "facebook", "twitter", "linkedin"]}
                      iconColor="var(--light-text)"
                      // itemStyles={{ padding: "0 var(--spacing-xs) 0 0" }}
                    />
                  </div>
                </IconBox>
              </div>

              {/* <div className={`${styles.contactLinks}`}>

              </div> */}
              <Link
                className={`${styles.attaLogo}`}
                href="https://www.adventuretravel.biz"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src="/assets/img/ATTA-Logo-BW.png"
                  fill
                  alt="Adventure Travel Trade Association logo"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
export default ContactUs;

export async function getStaticProps() {
  const { gql } = await import("@apollo/client");
  const { default: MarkdownIt } = await import("markdown-it");

  const strapiApolloClient = graphQlClient();
  const md = new MarkdownIt();

  const contactPageQuery = gql`
    query ContactPageQuery {
      contactPage {
        data {
          attributes {
            contactContent {
              contactEmail
              description
              socialIconLink1
              socialIconLink2
            }
            slug
            search_engine_optimization {
              meta {
                title
                content
              }
              metaDescription
              metaTitle
              preventIndexing
              metaMedia {
                data {
                  attributes {
                    url
                    alternativeText
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const contactPageResponse = await strapiApolloClient.query({
    query: contactPageQuery,
  });

  const contactPageData =
    await contactPageResponse?.data?.contactPage?.data?.attributes;

  const contactData = {
    ...contactPageData,
    contactContent: {
      ...contactPageData.contactContent,
      contactContent: md.render(contactPageData.contactContent.description),
    },
  };

  // const layoutData = await fetchLayoutData();
  const layoutData = await fetchLayoutDataServer();

  return {
    props: {
      contactData /*: data.contactPage*/,
      layoutData,
    },
  };
}
